import { gql } from '@sbvr/common/legacy-shared/api';
import DeviceGroup from '@/core/store/types/DeviceGroup';
import Device from '@/core/store/types/Device';

const deviceGroupFragment = `
  id
  title
  description
  deviceCount
`;

const deviceFragment = `
  id
  serialNumber
  title
  hardware {
    name
  }
  launcherEnabled
  powerStatus
  maintenanceModeEnabled
  deviceGroup {
    id
  }
  kioskModeContent {
    id
  }
  playing {
    id
  }
  games {
    id
  }
`;

export async function getDeviceGroups ({
  dispatch,
  getters,
}): Promise<DeviceGroup[]> {
  const query: string = `{
    deviceGroups {
      ${deviceGroupFragment}
    }
  }`;

  const { deviceGroups } = await gql.request(query);

  const deviceGroupsWithOpen = deviceGroups.map((deviceGroup: DeviceGroup) => {
    const payload = {
      ...deviceGroup,
      _open: false,
      _loading: true,
    };

    if (deviceGroup.deviceCount === 0) {
      payload._loading = false;
    } else if (deviceGroup.deviceCount < 30) {
      dispatch('getDevicesInGroup', deviceGroup.id);

      const deviceGroupInStore: DeviceGroup = getters['entities/DeviceGroup'](deviceGroup.id);

      const devicesInGroupAlready: boolean = deviceGroupInStore?.devices
        ? deviceGroupInStore.devices.length > 0
        : false;

      payload._open = true;
      payload._loading = !devicesInGroupAlready;
    }

    return payload;
  });

  dispatch('insertEntities', {
    name: 'DeviceGroup',
    payload: deviceGroupsWithOpen,
  });

  return deviceGroupsWithOpen;
}

export async function getDevicesInGroup (
  { dispatch },
  deviceGroupId: string
): Promise<Device[]> {
  const query: string = `query deviceGroup ($id: ID) {
    deviceGroup (id: $id) {
      id
      devices {
        ${deviceFragment}
      }
    }
  }`;

  const { deviceGroup } = await gql.request(query, {
    id: deviceGroupId,
  });

  dispatch('insertEntities', {
    name: 'DeviceGroup',
    payload: {
      ...deviceGroup,
      _loading: false,
    },
  });

  return deviceGroup.devices;
}

export async function searchDevices ({ dispatch, getters }, search: string): Promise<string[]> {
  const query: string = `query devices ($search: String) {
    devices (search: $search) {
      ${deviceFragment}
    }
  }`;

  const { devices } = await gql.request(query, {
    search,
  });

  const deviceIds = await dispatch('insertEntities', {
    name: 'Device',
    payload: devices,
  });

  const deviceGroupIds = devices.reduce((deviceGroupIds: string[], device: Device): string[] => {
    if (!deviceGroupIds.includes(device.deviceGroup.id)) deviceGroupIds.push(device.deviceGroup.id);
    return deviceGroupIds;
  }, []);
  const deviceGroups: DeviceGroup[] = getters['entities/DeviceGroup'](deviceGroupIds);
  deviceGroups.forEach(deviceGroup => {
    if (deviceGroup.id) {
      dispatch('insertEntities', {
        name: 'DeviceGroup',
        payload: {
          ...deviceGroup,
          _open: true,
        },
      });
    }
  });

  return deviceIds;
}

export async function storeDeviceGroup (
  { dispatch },
  deviceGroup: DeviceGroup
): Promise<DeviceGroup> {
  const query: string = `mutation storeDeviceGroup ($deviceGroup: DeviceGroupInput) {
    storeDeviceGroup (deviceGroup: $deviceGroup) {
      ${deviceGroupFragment}
    }
  }`;

  const { storeDeviceGroup } = await gql.request(query, {
    deviceGroup,
  });

  dispatch('insertEntities', {
    name: 'DeviceGroup',
    payload: storeDeviceGroup,
  });

  return storeDeviceGroup;
}

export async function storeDevice (
  { dispatch },
  device: Device
): Promise<Device> {
  const query: string = `mutation storeDevice ($device: DeviceInput) {
    storeDevice (device: $device) {
      ${deviceFragment}
    }
  }`;

  const { storeDevice } = await gql.request(query, {
    device,
  });

  if (device.deviceGroup) await dispatch('getDeviceGroups');

  dispatch('insertEntities', {
    name: 'Device',
    payload: storeDevice,
  });

  return storeDevice;
}
